import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Skeleton,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { getOfflineData } from "../../../utils/offline-services";
import { isCreateAllowed, isStoreRole } from "../../../utils/common-utils";
import MuiTable from "../../../../common-utilities/core/mui-table";
import PRCreateModal from "../../purchase-requisition/create-pr/pr-modal";
import { buildFilterParams } from "../dashboard-filters/utils";
import {
  getRelatedPoDetailColumns,
  renderPoReleaseStatusChip,
  renderRowGrnStatusChip,
  renderRowInvoiceStatusChip,
  renderStatusChip,
  PR_STATUS_CHIP_PROPS,
} from "./helper";

import "./index.scss";

const RECENT_PR_LIMIT = 5;

const MyPrTracking = ({ onViewAll, filters, prTrackingSummary }) => {
  const navigate = useNavigate();
  const user = getOfflineData("user") || {};
  const userId = user?._id || user?.id;
  const canView = isCreateAllowed("pr");

  const [prListState, setPrListState] = useState("idle");
  const [prRows, setPrRows] = useState([]);
  const [rowState, setRowState] = useState({});
  const [poDetailState, setPoDetailState] = useState({});
  const [expandedPrId, setExpandedPrId] = useState(null);
  const [prModalOpen, setPRModalOpen] = useState(false);

  const loadPrList = () => {
    setPrListState("loading");
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}`,
      null,
      {
        limit: RECENT_PR_LIMIT,
        page: 1,
        sortBy: "-createdAt",
        createdBy: userId,
        ...buildFilterParams(filters),
      }
    )
      .then((response) => {
        if (response?.message) {
          setPrListState("error");
          return;
        }
        setPrRows(response?.results || []);
        setPrListState("loaded");
      })
      .catch(() => setPrListState("error"));
  };

  useEffect(() => {
    if (!canView) return;
    loadPrList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, filters?.financialYear, filters?.region, filters?.site, filters?.vendorCode]);

  // Step C — fetches a PO's GRN/invoice rollup. Triggered eagerly for every
  // linked PO as soon as Step B resolves (below), since the summary row's
  // GRN Status / Invoice Status columns need the real rollup, not a
  // placeholder — expanding a row never has to fetch anything new.
  const hydratePoDetails = (purchaseOrderIds = []) => {
    purchaseOrderIds.forEach((po) => {
      const poId = po.id || po._id;
      if (!poId || poDetailState[poId]) return;

      setPoDetailState((prev) => ({
        ...prev,
        [poId]: { grn: { status: "loading", records: [] }, invoice: { status: "loading", records: [] } },
      }));

      invokeApi(HTTP_METHODS.GET, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GRN_LIST}`, null, {
        searchKey: po.poNumber,
        limit: 50,
        page: 1,
      })
        .then((response) => {
          if (response?.message) throw new Error(response.message);
          setPoDetailState((prev) => ({
            ...prev,
            [poId]: { ...prev[poId], grn: { status: "loaded", records: response?.results || [] } },
          }));
        })
        .catch(() => {
          setPoDetailState((prev) => ({
            ...prev,
            [poId]: { ...prev[poId], grn: { status: "error", records: [] } },
          }));
        });

      invokeApi(HTTP_METHODS.GET, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.INVOICE_DATA}`, null, {
        poGrnNo: po.poNumber,
        limit: 50,
        page: 1,
      })
        .then((response) => {
          if (response?.message) throw new Error(response.message);
          setPoDetailState((prev) => ({
            ...prev,
            [poId]: { ...prev[poId], invoice: { status: "loaded", records: response?.results || [] } },
          }));
        })
        .catch(() => {
          setPoDetailState((prev) => ({
            ...prev,
            [poId]: { ...prev[poId], invoice: { status: "error", records: [] } },
          }));
        });
    });
  };

  // Step B — hydrate each (small, ≤ RECENT_PR_LIMIT) row's linked POs, in
  // parallel, right after the list loads, then kick off Step C for those POs
  // immediately (not gated on expansion) so the summary row's GRN/Invoice
  // Status columns reflect real data as soon as it's available.
  useEffect(() => {
    if (!prRows.length) return;

    prRows.forEach((pr) => {
      const prId = pr.id || pr._id;
      if (!prId || rowState[prId]) return;

      setRowState((prev) => ({ ...prev, [prId]: { status: "loading", purchaseOrderIds: [] } }));

      invokeApi(
        HTTP_METHODS.GET,
        `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}/${prId}`
      )
        .then((detail) => {
          if (detail?.message) throw new Error(detail.message);
          const purchaseOrderIds = detail?.purchaseOrderIds || [];
          setRowState((prev) => ({
            ...prev,
            [prId]: { status: "loaded", purchaseOrderIds },
          }));
          hydratePoDetails(purchaseOrderIds);
        })
        .catch(() => {
          setRowState((prev) => ({ ...prev, [prId]: { status: "error", purchaseOrderIds: [] } }));
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prRows]);

  const handleAccordionChange = (pr) => (event, isExpanded) => {
    const prId = pr.id || pr._id;
    setExpandedPrId(isExpanded ? prId : null);
  };

  const handleViewAll = () => {
    if (typeof onViewAll === "function") {
      onViewAll();
      return;
    }
    navigate("purchase-requisition");
  };

  const handleViewProgress = (pr) => {
    navigate(`purchase-requisition/${pr.id || pr._id}`);
  };

  if (!canView) return null;

  return (
    <Box className="finance-v2-my-pr-tracking-section">
      <Box className="my-pr-tracking-header">
        <Typography className="my-pr-tracking-title">My PR Tracking</Typography>
        <Button
          className="my-pr-tracking-view-all"
          variant="outlined"
          size="small"
          onClick={handleViewAll}
        >
          View All
        </Button>
      </Box>

      <Box className="my-pr-tracking-summary-tiles">
        <Box className="my-pr-tracking-cell my-pr-tracking-summary-cell">
          <Typography className="my-pr-tracking-cell-label">PO Release Summary</Typography>
          <Typography className="my-pr-tracking-cell-value">
            {prTrackingSummary?.poReleasedCount ?? 0} POs from {prTrackingSummary?.approvedPrWithReleasedPoCount ?? 0} approved PRs
          </Typography>
        </Box>
        <Box className="my-pr-tracking-cell my-pr-tracking-summary-cell">
          <Typography className="my-pr-tracking-cell-label">Pending Conversion</Typography>
          <Typography className="my-pr-tracking-cell-value">
            {prTrackingSummary?.approvedPrPendingPoCreationCount ?? 0} PRs pending PO creation
          </Typography>
        </Box>
      </Box>

      {prListState === "loading" && (
        <Box className="my-pr-tracking-skeletons">
          {Array.from({ length: 3 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" className="my-pr-tracking-skeleton-row" />
          ))}
        </Box>
      )}

      {prListState === "error" && (
        <Box className="my-pr-tracking-empty">
          <Typography className="my-pr-tracking-empty-text">
            Couldn't load your PR tracking data.
          </Typography>
          <Button variant="outlined" size="small" onClick={loadPrList}>
            Retry
          </Button>
        </Box>
      )}

      {prListState === "loaded" && prRows.length === 0 && (
        <Box className="my-pr-tracking-empty">
          <Typography className="my-pr-tracking-empty-text">
            You haven't created any Purchase Requisitions yet.
          </Typography>
          {!isStoreRole() && (
            <Button variant="outlined" size="small" onClick={() => setPRModalOpen(true)}>
              Create PR
            </Button>
          )}
        </Box>
      )}

      {prListState === "loaded" && prRows.length > 0 && (
        <Box className="my-pr-tracking-list">
          {prRows.map((pr) => {
            const prId = pr.id || pr._id;
            const currentRowState = rowState[prId];

            return (
              <Accordion
                key={prId}
                className="my-pr-tracking-accordion"
                expanded={expandedPrId === prId}
                onChange={handleAccordionChange(pr)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className="my-pr-tracking-summary"
                >
                  <Box className="my-pr-tracking-row">
                    <Box className="my-pr-tracking-cell my-pr-tracking-cell-pr-number">
                      <Typography className="my-pr-tracking-cell-label">PR Number</Typography>
                      <Typography className="my-pr-tracking-cell-value">
                        {pr.prNumber || prId}
                      </Typography>
                    </Box>

                    <Box className="my-pr-tracking-cell">
                      <Typography className="my-pr-tracking-cell-label">PR Status</Typography>
                      {renderStatusChip(PR_STATUS_CHIP_PROPS, pr.status)}
                    </Box>

                    <Box className="my-pr-tracking-cell">
                      <Typography className="my-pr-tracking-cell-label">PO Release Status</Typography>
                      {renderPoReleaseStatusChip(pr)}
                    </Box>

                    <Box className="my-pr-tracking-cell">
                      <Typography className="my-pr-tracking-cell-label">GRN Status</Typography>
                      {renderRowGrnStatusChip(currentRowState, poDetailState)}
                    </Box>

                    <Box className="my-pr-tracking-cell">
                      <Typography className="my-pr-tracking-cell-label">Invoice Status</Typography>
                      {renderRowInvoiceStatusChip(currentRowState, poDetailState)}
                    </Box>

                    <Box className="my-pr-tracking-cell my-pr-tracking-cell-action">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleViewProgress(pr);
                        }}
                      >
                        View Progress
                      </Button>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails className="my-pr-tracking-details">
                  {(!currentRowState || currentRowState.status === "loading") && (
                    <Typography className="my-pr-tracking-details-hint">
                      Loading purchase orders…
                    </Typography>
                  )}

                  {currentRowState?.status === "error" && (
                    <Typography className="my-pr-tracking-details-hint">
                      Unable to load purchase orders for this PR.
                    </Typography>
                  )}

                  {currentRowState?.status === "loaded" &&
                    (currentRowState.purchaseOrderIds || []).length === 0 && (
                      <Typography className="my-pr-tracking-details-hint">
                        No purchase orders have been created against this PR yet.
                      </Typography>
                    )}

                  {currentRowState?.status === "loaded" &&
                    (currentRowState.purchaseOrderIds || []).length > 0 && (
                      <MuiTable
                        columnsList={getRelatedPoDetailColumns(navigate, poDetailState)}
                        dataList={currentRowState.purchaseOrderIds}
                      />
                    )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {prModalOpen && (
        <PRCreateModal
          onClose={() => {
            setPRModalOpen(false);
            loadPrList();
          }}
        />
      )}
    </Box>
  );
};

export default MyPrTracking;
